import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { audioEngine } from '@/audio/engine/AudioEngine';
import type { AudioEngineState } from '@/types/audio';
import type { Observable } from '@/training/observable';

/** Throttled microphone state. Updates at most 10 times per second. */
export function useAudioState(): AudioEngineState {
  return useSyncExternalStore(audioEngine.subscribe, audioEngine.getSnapshot, audioEngine.getSnapshot);
}

/** Subscribe to any engine's snapshot store. */
export function useEngineStore<T>(store: Observable<T>): T {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

/**
 * Declare that this screen needs the microphone.
 *
 * The engine keeps the stream alive while at least one screen holds it and
 * shuts it down shortly after the last one unmounts, so the recording indicator
 * does not stay lit — and a phone does not keep running FFTs — while the player
 * is reading the chord library.
 */
export function useMicrophoneLease(): void {
  useEffect(() => audioEngine.acquire(), []);
}

/**
 * Ask for the microphone.
 *
 * `enable` must be called directly from a user gesture: Safari does not count
 * the answer to the permission prompt as one, so the AudioContext has to be
 * created inside the click itself (see `AudioEngine.start`).
 */
export function useMicrophone() {
  const state = useAudioState();

  const enable = useCallback(() => {
    void audioEngine.start();
  }, []);

  return { state, enable, engine: audioEngine };
}

/**
 * Keep the AudioContext honest across tab switches and iOS screen locks.
 * Without this the context silently suspends and the session records nothing.
 */
export function useAudioLifecycle(onHidden?: () => void): void {
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        onHidden?.();
      } else {
        void audioEngine.resume();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [onHidden]);
}
